import { useEffect, useRef } from "react";
import Marquee from "react-fast-marquee";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { TICKER_ITEMS, FALSE_BELIEFS, HOST, scrollToId } from "../lib/content";

gsap.registerPlugin(ScrollTrigger);

export const Ticker = () => (
  <section className="border-y border-black/10 py-4" data-testid="proof-ticker">
    <Marquee speed={38} gradient={false} autoFill>
      {TICKER_ITEMS.map((item) => (
        <span key={item} className="font-mono-c text-[11px] tracking-[0.2em] text-black/40 flex items-center">
          <span className="mx-6" style={{ color: "#7C3AED" }}>◆</span>
          {item}
        </span>
      ))}
    </Marquee>
  </section>
);

const MANIFESTO_LINES = [
  { text: "FIRST-TIME HOME BUYING", cls: "text-[#181818]" },
  { text: "SHOULD NOT FEEL", cls: "text-black/[0.28]" },
  { text: "LIKE DECODING", cls: "text-[#181818]" },
  { text: "FINE PRINT.", cls: "text-[#7C3AED]" },
];

export const Manifesto = () => {
  const ref = useRef(null);

  useEffect(() => {
    const words = ref.current.querySelectorAll(".mword");
    const tween = gsap.fromTo(words,
      { opacity: 0, y: 26 },
      { opacity: 1, y: 0, stagger: 0.04, duration: 0.65, ease: "power3.out",
        scrollTrigger: { trigger: ref.current, start: "top 72%" } }
    );
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);

  return (
    <section className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden" style={{ background: "#F2EEFB" }} data-testid="manifesto-section">
      <div className="max-w-[1440px] mx-auto">
      <p className="chapter-label mb-8">[001] — THE PROBLEM</p>
      <h2 ref={ref} className="font-display font-bold uppercase max-w-4xl" style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 0.97, letterSpacing: "-0.02em" }}>
        {MANIFESTO_LINES.map((line, li) => (
          <span key={li} className={`block ${line.cls}`}>
            {line.text.split(" ").map((w, wi) => (
              <span key={wi} className="mword inline-block mr-[0.24em]">{w}</span>
            ))}
          </span>
        ))}
      </h2>
      <Reveal delay={0.1}>
        <p className="mt-10 text-base leading-[1.75] text-black/55 max-w-[560px]">
          For first-time buyers in Nevada and Texas earning good money, the process still feels rigged — acronyms, conflicting advice, and big banks that say "not yet" without explaining why. This 45-minute session translates all of it into plain numbers: what you can actually afford, which programs fit your income type, and exactly what to prepare. No jargon, no pressure, no application.
        </p>
        <button onClick={() => scrollToId("register")} className="btn-outline-accent mt-8 px-7 py-3.5 text-[12px]" data-testid="manifesto-cta">
          GET CLARITY — FREE →
        </button>
      </Reveal>
      </div>
    </section>
  );
};

export const Letter = () => (
  <section className="py-28 md:py-36 px-6" style={{ background: "#FFFFFF" }} data-testid="letter-section">
    <div className="max-w-[760px] mx-auto">
      <Reveal><p className="chapter-label mb-8">[002] — A LETTER FROM VELISA</p></Reveal>
      <Reveal delay={0.05}>
        <p className="font-display text-2xl text-black/60 italic mb-10">Future Homeowner,</p>
        <p className="text-[15px] leading-[1.8] text-black/55 mb-10">
          Every week I talk to renters in Nevada and Texas who could qualify for a home right now — but don't believe it. Not because of their income. Because of three myths they've been carrying around for years:
        </p>
      </Reveal>
      <div className="space-y-4 mb-10">
        {FALSE_BELIEFS.map((belief, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="flex items-start gap-4 p-5" style={{ background: "rgba(255,0,0,0.04)", border: "1px solid rgba(255,0,0,0.1)" }} data-testid={`false-belief-${i}`}>
              <X size={18} className="mt-0.5 shrink-0 text-red-500" />
              <p className="text-[15px] italic text-black/75">{belief}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="h-px w-24 mb-10" style={{ background: "#7C3AED" }} />
        <div className="space-y-6 text-[15px] leading-[1.8] text-black/55">
          <p>
            Here's what working inside the lending process has taught me: the 20% figure is a rule of thumb from another era — many first-time buyer programs are built around far smaller down payments, and some buyers qualify with much less than they assumed.
          </p>
          <p>
            "Perfect credit" isn't the bar either. Lenders look at a full picture — income stability, debt ratios, and documented history — and the factors that actually move the needle are often ones no one has ever explained to you.
          </p>
          <p>
            And waiting? Waiting without a plan is usually the most expensive choice of all. The buyers who win aren't the ones who saved the longest — they're the ones who understood their numbers first. That's exactly what this free session gives you, with no application and no credit pull.
          </p>
        </div>
        <p className="mt-10 font-display font-semibold" style={{ color: "#7C3AED" }} data-testid="letter-signoff">
          — {HOST.name}, {HOST.company}
        </p>
      </Reveal>
    </div>
  </section>
);
